import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://rotaxengineering.com';
const DEFAULT_IMAGE_URL = `${SITE_URL}/rotaxlogo.png`;

function ensureMeta({ kind, attr, value }) {
  const selector = kind === 'name' ? `meta[name="${value}"]` : `meta[property="${value}"]`;
  const el = document.head.querySelector(selector);
  if (el) return el;

  const meta = document.createElement('meta');
  meta.setAttribute(attr, value);
  document.head.appendChild(meta);
  return meta;
}

function ensureLinkCanonical() {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (el) return el;
  el = document.createElement('link');
  el.setAttribute('rel', 'canonical');
  document.head.appendChild(el);
  return el;
}

export default function Seo({
  title,
  description,
  keywords,
  imageUrl = DEFAULT_IMAGE_URL
}) {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname === '/' ? '' : location.pathname;
    const url = `${SITE_URL}${path}`;

    if (title) document.title = title;

    // Description
    if (description) {
      const metaDescription = ensureMeta({ kind: 'name', attr: 'name', value: 'description' });
      metaDescription.setAttribute('content', description);
    }

    // Keywords
    if (keywords) {
      const metaKeywords = ensureMeta({ kind: 'name', attr: 'name', value: 'keywords' });
      metaKeywords.setAttribute('content', keywords);
    }

    // Canonical + URL-dependent tags
    const canonical = ensureLinkCanonical();
    canonical.setAttribute('href', url);

    const ensureProperty = (propertyName) =>
      ensureMeta({ kind: 'property', attr: 'property', value: propertyName });

    // OpenGraph
    const ogTitle = ensureProperty('og:title');
    if (title) ogTitle.setAttribute('content', title);

    const ogDescription = ensureProperty('og:description');
    if (description) ogDescription.setAttribute('content', description);

    const ogUrl = ensureProperty('og:url');
    ogUrl.setAttribute('content', url);

    const ogImage = ensureProperty('og:image');
    ogImage.setAttribute('content', imageUrl);

    const ogImageAlt = ensureProperty('og:image:alt');
    if (title) ogImageAlt.setAttribute('content', title);

    const ogSiteName = ensureProperty('og:site_name');
    ogSiteName.setAttribute('content', 'Rotax Engineering CC');

    // Twitter
    const ensureTwitter = (name) => ensureMeta({ kind: 'name', attr: 'name', value: name });
    const twitterCard = ensureTwitter('twitter:card');
    twitterCard.setAttribute('content', 'summary_large_image');

    const twitterTitle = ensureTwitter('twitter:title');
    if (title) twitterTitle.setAttribute('content', title);

    const twitterDescription = ensureTwitter('twitter:description');
    if (description) twitterDescription.setAttribute('content', description);

    const twitterImage = ensureTwitter('twitter:image');
    twitterImage.setAttribute('content', imageUrl);
  }, [title, description, keywords, imageUrl, location.pathname]);

  return null;
}

